import { fieldProblemReasonLabels, type FieldMissionItem } from '@groupe-reca/contracts'
import { useEffect, useState, type ReactNode } from 'react'
import { ArrowLeftCircle, ArrowRightCircle, FileText, TriangleAlert } from 'lucide-react'
import { formatDuration as formatClock } from '@/features/detection/presentation/describeEvent'
import { formatTime, itemAddress } from '../../domain/fieldText'
import { estimateLabel, type TravelEstimate } from '../../domain/map/fieldMeasures'
import type { StreetSide } from '../../domain/map/streetSide'
import { focusLabels, type Focus } from '../focus'
import { HoldButton, type HoldPhase } from './HoldButton'

// Résidence mise en avant (maquettes 1a « bloc actif + liste » et 1b « focus résidence »). En observation, le moteur
// ne décide rien : le maintien de 3 s reste la seule confirmation du travail.

export type PanelVariant = '1a' | '1b'

export interface FocusCardProps {
  focus: Focus
  variant: PanelVariant
  observing: boolean
  paused: boolean
  phase: HoldPhase
  message: { kind: 'short' | 'error'; text: string } | undefined
  onOpen: (item: FieldMissionItem) => void
  /** Ouvre la fiche sur le signalement à motif obligatoire (même commande serveur que la fiche). */
  onProblem: (item: FieldMissionItem) => void
  /** Pied de carte : aperçu de la résidence suivante et accès à la liste. */
  footer?: ReactNode
  onConfirm: (item: FieldMissionItem) => void
  onShortHold: (item: FieldMissionItem) => void
  /** Côté de la rue (sens d'approche prévu) et estimation qualifiée; `null` : rien de fiable à montrer. */
  approach?: { side: StreetSide | null; estimate: TravelEstimate | null } | null
}

/** Estimation avec sa propre horloge (30 s) : l'heure d'arrivée avance sans redessiner la carte. */
function EstimateLine({ estimate }: { estimate: TravelEstimate }) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date())
    }, 30_000)
    return () => {
      clearInterval(timer)
    }
  }, [])
  return (
    <p className="op-focus__estimate">
      {estimateLabel(estimate, now)}
      {estimate.kind === 'ROUTE' ? <small> · estimation voiture, non calibrée tracteur</small> : null}
    </p>
  )
}

function instructions(item: FieldMissionItem): string[] {
  return [item.locationInstructions, item.operatorMessage, item.specialInstructions, item.knownObstacles].filter(
    (value): value is string => Boolean(value?.trim()),
  )
}

export function FocusCard({
  focus,
  variant,
  observing,
  paused,
  phase,
  message,
  onOpen,
  onProblem,
  footer,
  onConfirm,
  onShortHold,
  approach = null,
}: FocusCardProps) {
  const { item, tone } = focus
  const address = itemAddress(item.snapshot)
  const notes = instructions(item)
  const timed = tone === 'capture' || tone === 'working' || tone === 'suspended'
  const lastProblem = [...item.events].reverse().find((event) => event.kind === 'PROBLEM_REPORTED')

  return (
    <section
      className={`op-focus op-focus--${tone} op-focus--${variant}`}
      aria-label={`${focusLabels[tone]} : ${address}`}
    >
      <div className="op-focus__top">
        <span className={`op-focus__chip op-focus__chip--${tone}`}>{focusLabels[tone]}</span>
        {focus.distanceM !== null ? <span className="op-focus__distance">{focus.distanceM} m</span> : null}
        {observing && tone !== 'next' ? <span className="op-focus__observing">Observation</span> : null}
      </div>
      <p className="op-focus__address">
        <span className="op-order">{item.order}</span> {address}
      </p>
      {item.snapshot.clientName ? <p className="op-focus__name">{item.snapshot.clientName}</p> : null}
      {!timed && approach?.side ? (
        <p className={`op-focus__side op-focus__side--${approach.side.toLowerCase()}`}>
          {approach.side === 'LEFT' ? <ArrowLeftCircle aria-hidden="true" /> : <ArrowRightCircle aria-hidden="true" />}
          {approach.side === 'LEFT' ? 'À gauche' : 'À droite'}
        </p>
      ) : null}
      {!timed && approach?.estimate ? <EstimateLine estimate={approach.estimate} /> : null}

      {timed && focus.observedMs !== null ? (
        <div className={`op-focus__timer${tone === 'suspended' ? ' is-suspended' : ''}`}>
          <span className="op-focus__timer-label">
            {tone === 'suspended' ? 'Temps observé · ajout suspendu' : 'Temps observé'}
          </span>
          <strong aria-live="off">{formatClock(focus.observedMs)}</strong>
          {tone !== 'suspended' ? (
            <span className="op-focus__timer-note">
              {observing
                ? 'Le moteur proposerait la fin au départ confirmé ou à l’entrée de la résidence suivante.'
                : 'Se termine au départ confirmé ou à l’entrée de la résidence suivante.'}
            </span>
          ) : null}
        </div>
      ) : null}

      {tone === 'problem' && lastProblem?.reasonCode ? (
        <div className="op-focus__problem" role="note">
          <strong>
            <TriangleAlert aria-hidden="true" /> {fieldProblemReasonLabels[lastProblem.reasonCode]}
          </strong>
          <span>
            Signalé à {formatTime(lastProblem.occurredAt)}. Reste en problème jusqu’à résolution; le GPS ne l’efface pas.
          </span>
        </div>
      ) : null}

      {notes.length > 0 ? (
        <div className="op-focus__notes">
          {variant === '1b' ? <span className="field-eyebrow">Consignes</span> : null}
          {notes.map((note) => (
            <p key={note}>{note}</p>
          ))}
        </div>
      ) : null}

      <div className="op-focus__actions">
        <button
          type="button"
          className="field-button field-button--outline"
          aria-label={`Ouvrir la fiche du ${address}`}
          onClick={() => {
            onOpen(item)
          }}
        >
          <FileText aria-hidden="true" /> Fiche client
        </button>
        {!paused && item.status === 'WAITING' ? (
          <button
            type="button"
            className="field-button field-button--attempt"
            aria-label={`Signaler un problème au ${address}`}
            onClick={() => {
              onProblem(item)
            }}
          >
            <TriangleAlert aria-hidden="true" /> Problème
          </button>
        ) : null}
        {!paused ? (
          <div className="op-focus__hold">
            <span className="op-focus__hold-label">
              {tone === 'problem' ? 'Résoudre le problème : maintenez 3 s' : 'Travail fait : maintenez 3 s'}
            </span>
            <HoldButton
              address={address}
              phase={phase}
              onConfirm={() => {
                onConfirm(item)
              }}
              onCancel={() => {
                onShortHold(item)
              }}
            />
          </div>
        ) : null}
      </div>
      {message ? (
        <div className={`field-card__message field-card__message--${message.kind}`} role="status">
          {message.text}
        </div>
      ) : null}
      {footer}
    </section>
  )
}
